class RequestManager {
  controller = new AbortController();
  currentAbortableThunkSignal = null;
  keyedControllers = new Map();
  pageResetListeners = new Set();
  // Track silent page reset per request key (endpoint). This prevents cross-screen interference.
  _silentPageResetPendingKeys = new Set();

  reset() {
    // Abort the global controller (for direct axios requests without their own cancellation)
    this.controller.abort();
    this.controller = new AbortController();
    // Clear the current abortableThunk signal
    this.currentAbortableThunkSignal = null;
    // Abort any keyed controllers (per-endpoint controllers)
    this.keyedControllers.forEach(({ controller }) => controller.abort());
    this.keyedControllers.clear();
  }

  get signal() {
    return this.controller.signal;
  }

  setAbortableThunkSignal(signal) {
    this.currentAbortableThunkSignal = signal;
  }

  clearAbortableThunkSignal() {
    this.currentAbortableThunkSignal = null;
  }

  /**
   * Normalizes params by removing page-related params for comparison.
   * Returns a string representation of non-page params.
   */
  normalizeParamsForComparison(params) {
    if (!params || typeof params !== 'object') return '';
    const normalized = { ...params };
    delete normalized.page;
    delete normalized.itemsPerPage;
    // Sort keys for consistent comparison
    const sortedKeys = Object.keys(normalized).sort();
    const sorted = {};
    sortedKeys.forEach(key => {
      sorted[key] = normalized[key];
    });
    return JSON.stringify(sorted);
  }

  /**
   * Checks if params changed (excluding page/itemsPerPage) and returns updated params with page=1 if needed.
   */
  checkAndResetPageIfParamsChanged(key, currentParams) {
    const prev = this.keyedControllers.get(key);
    const currentNormalized = this.normalizeParamsForComparison(currentParams);
    const prevNormalized = prev?.lastParams ? this.normalizeParamsForComparison(prev.lastParams) : null;

    // If non-page params changed, set page=1
    if (prevNormalized !== null && currentNormalized !== prevNormalized) {
      const updatedParams = { ...currentParams, page: 1 };
      return { paramsChanged: true, updatedParams };
    }

    return { paramsChanged: false, updatedParams: currentParams };
  }

  /**
   * Abort + replace an AbortController for a stable key (e.g. "get:/api/v1/purchase-orders").
   * Returns a signal that is also tied to the global controller, so route changes still cancel it.
   * Also tracks params to detect when non-page params change.
   */
  nextSignalForKey(key, params = null) {
    const prev = this.keyedControllers.get(key);
    if (prev?.controller) {
      prev.controller.abort();
    }

    const controller = new AbortController();
    this.keyedControllers.set(key, { controller, lastParams: params });

    return this.combineSignals(controller.signal, this.controller.signal);
  }

  /**
   * Subscribe to page reset events. Returns an unsubscribe function.
   */
  onPageReset(callback) {
    this.pageResetListeners.add(callback);
    return () => {
      this.pageResetListeners.delete(callback);
    };
  }

  /**
   * Emit page reset event to all listeners.
   * Sets the silent flag to mark this as a system-driven reset.
   */
  emitPageReset(key) {
    if (key) {
      this._silentPageResetPendingKeys.add(key);
    }
    this.pageResetListeners.forEach(callback => {
      try {
        callback(key);
      } catch (error) {
        console.error('Error in page reset listener:', error);
      }
    });
  }

  /**
   * Clear stale silent flag. Called when a user-initiated request is made on page 1.
   * This prevents stale flags from blocking legitimate requests.
   */
  clearStaleSilentFlag(key) {
    if (!key) return;
    this._silentPageResetPendingKeys.delete(key);
  }

  /**
   * Check if the current page reset is silent (system-driven).
   * If true, clears the flag and returns true.
   * Components should skip data-fetching when this returns true.
   * The flag persists until explicitly checked and cleared.
   * 
   * @param {string} key - Request key for the current screen (e.g. "get:/api/v1/purchase-orders")
   * @param {number} currentPage - Current page value from component state
   * @returns {boolean} True if request should be skipped
   */
  checkAndClearSilentFlag(key, currentPage = null) {
    if (!key) return false;
    if (this._silentPageResetPendingKeys.has(key)) {
      // Only skip if currentPage is actually 1 (meaning the reset happened)
      if (currentPage === 1) {
        this._silentPageResetPendingKeys.delete(key);
        return true;
      }
      // If currentPage is not 1, the flag is stale for this key; clear it.
      this._silentPageResetPendingKeys.delete(key);
      return false;
    }
    return false;
  }

  /**
   * Combines multiple AbortSignals into one.
   * The returned signal will abort when any of the input signals abort.
   */
  combineSignals(...signals) {
    const combinedController = new AbortController();
    
    // If any signal is already aborted, abort the combined controller immediately
    if (signals.some(signal => signal.aborted)) {
      combinedController.abort();
      return combinedController.signal;
    }

    // Listen to each signal and abort the combined controller when any one aborts
    const abortHandler = () => {
      combinedController.abort();
      // Clean up listeners
      signals.forEach(signal => {
        signal.removeEventListener('abort', abortHandler);
      });
    };

    signals.forEach(signal => {
      signal.addEventListener('abort', abortHandler);
    });

    return combinedController.signal;
  }
}

export const requestManager = new RequestManager();
